// v10/src/components/admin/AdminAudioCache.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Volume2, Download, Trash2, RefreshCw, CheckCircle2,
  AlertCircle, Loader, Plane, Clock, Wifi, WifiOff, ChevronDown, ChevronUp
} from 'lucide-react';
import { aircraftService, checklistService } from '../../services/apiService';
import { fetchAndStoreManifest, getAudioCacheInfo, clearAudioCache } from '../../utils/speechHelper';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthToken = () => {
  try {
    const raw = localStorage.getItem('condor360_current_user')
             || sessionStorage.getItem('condor360_current_user');
    return JSON.parse(raw)?.token || '';
  } catch { return ''; }
};

// ── Tarjeta por aeronave ──────────────────────────────────────────
const AircraftCacheCard = ({ aircraft, darkMode, onGenerated, isDemo }) => {
  const [loading,    setLoading]    = useState(false);
  const [progress,   setProgress]   = useState('');
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState('');
  const [showItems,  setShowItems]  = useState(false);
  const [cacheInfo,  setCacheInfo]  = useState({ cached: false, count: 0, offlineCount: 0, updatedAt: null });
  const [totalItems, setTotalItems] = useState(null);
  const [jobProgress, setJobProgress] = useState(null); // { done, total }
  // ✅ Estado de feedback para el botón Sincronizar
  const [syncStatus, setSyncStatus] = useState(''); // '' | 'syncing' | 'success' | 'error'
  const pollRef = useRef(null);

  useEffect(() => {
    checklistService.getByAircraft(aircraft.id)
      .then(res => {
        const sections = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        const count = sections.reduce((acc, s) => acc + (s.items?.length || 0), 0);
        setTotalItems(count);
      })
      .catch(() => setTotalItems(0));
  }, [aircraft.id]);

  const refreshCacheInfo = () => getAudioCacheInfo(aircraft.id).then(setCacheInfo);

  useEffect(() => { refreshCacheInfo(); }, [aircraft.id]); // eslint-disable-line

  // Polling al status del job — se activa cuando arrancamos uno o si al
  // montar detectamos uno en curso (cambio reciente del checklist).
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const fetchJobStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/tts/pregenerate-status/${aircraft.id}`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  };

  const finishJob = async (data) => {
    stopPolling();
    setLoading(false);
    setProgress('');
    setJobProgress(null);
    setResult({
      generated: data.generated ?? 0,
      total:     data.total ?? 0,
      errors:    data.errors ?? 0,
      errorList: data.errorList || [],
    });
    if (data.errors > 0 && (data.generated ?? 0) === 0) {
      setError(data.errorList?.[0]?.error || 'No se pudo generar audios.');
    }
    await fetchAndStoreManifest(aircraft.id);
    refreshCacheInfo();
    onGenerated?.(aircraft.id);
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const data = await fetchJobStatus();
      if (!data) return;
      if (data.status === 'running') {
        setJobProgress({ done: data.done ?? 0, total: data.total ?? 0 });
        const voiceName = localStorage.getItem('condor360_language') === 'en' ? 'Lauren' : 'Valentina';
        setProgress(`Generando voces con ${voiceName}: ${data.done ?? 0}/${data.total ?? 0}`);
      }
      if (data.status === 'completed' || data.status === 'error') {
        await finishJob(data);
      }
    }, 1500);
  };

  // Al montar / cambiar de aeronave: si hay job en curso, lo enganchamos.
  useEffect(() => {
    fetchJobStatus().then(data => {
      if (data && (data.status === 'running' || data.status === 'pending')) {
        setLoading(true);
        setJobProgress({ done: data.done ?? 0, total: data.total ?? 0 });
        startPolling();
      }
    });
    return stopPolling;
  }, [aircraft.id]);

  const handleGenerate = async () => {
    setLoading(true); setError(''); setResult(null); setJobProgress(null);
    const voiceName = localStorage.getItem('condor360_language') === 'en' ? 'Lauren' : 'Valentina';
    setProgress(`Iniciando generación con ${voiceName}…`);
    try {
      // ?force=true preserva la semántica del botón manual: regen completa
      const res = await fetch(`${API_BASE}/api/tts/pregenerate/${aircraft.id}?force=true`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al generar');

      if (data.status === 'running') {
        setJobProgress({ done: 0, total: data.total ?? 0 });
        setProgress(`Generando voces con ${voiceName}: 0/${data.total ?? 0}`);
        startPolling();
      } else {
        // Caso edge: el server respondió completed inmediato (nada que hacer)
        await finishJob(data);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setProgress('');
    }
  };

  const handleClearCache = () => {
    clearAudioCache(aircraft.id);
    refreshCacheInfo();
    setResult(null);
  };

  // ✅ handleRefreshManifest con feedback de estado
  const handleRefreshManifest = async () => {
    setSyncStatus('syncing');
    try {
      await fetchAndStoreManifest(aircraft.id);
      refreshCacheInfo();
      setSyncStatus('success');
      setTimeout(() => setSyncStatus(''), 3000);
    } catch (err) {
      setSyncStatus('error');
      setTimeout(() => setSyncStatus(''), 3000);
    }
  };

  const hasCache  = cacheInfo.cached && cacheInfo.count > 0;
  const isLoading = totalItems === null;
  const coverage  = totalItems > 0 ? Math.round((cacheInfo.count / totalItems) * 100) : 0;
  const isStale   = hasCache
    && aircraft.checklist_last_updated
    && cacheInfo.updatedAt
    && new Date(aircraft.checklist_last_updated) > new Date(cacheInfo.updatedAt);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{
        borderRadius: '20px', overflow: 'hidden',
        border: `1px solid ${darkMode ? 'rgba(55,65,81,1)' : 'rgba(229,231,235,1)'}`,
        background: darkMode ? 'rgba(31,41,55,1)' : 'white',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: `1px solid ${darkMode ? 'rgba(55,65,81,1)' : 'rgba(243,244,246,1)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '12px', flexShrink: 0,
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Plane style={{ width: '18px', height: '18px', color: 'white' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: '14px', color: darkMode ? 'white' : '#111827' }}>
              {aircraft.manufacturer} {aircraft.model_name}
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(107,114,128,1)' }}>
              {aircraft.registration || aircraft.id} · {
                isLoading
                  ? <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>cargando...</span>
                  : `${totalItems} ítem${totalItems !== 1 ? 's' : ''} en checklist`
              }
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {hasCache ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
              background: isStale ? 'rgba(245,158,11,0.1)' : 'rgba(34,197,94,0.1)',
              color: isStale ? '#d97706' : '#16a34a',
              border: `1px solid ${isStale ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.25)'}`,
            }}>
              <WifiOff style={{ width: '10px', height: '10px' }} />
              {isStale ? 'Desactualizado' : `${coverage}% offline`}
            </span>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
              background: 'rgba(245,158,11,0.1)', color: '#d97706',
              border: '1px solid rgba(245,158,11,0.25)',
            }}>
              <Wifi style={{ width: '10px', height: '10px' }} />
              Solo online
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px' }}>

        {/* Barra de coverage */}
        {hasCache && !isLoading && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(107,114,128,1)' }}>
                {cacheInfo.count} de {totalItems} audios cacheados
              </span>
              <span style={{ fontSize: '12px', fontWeight: 700, color: coverage === 100 ? '#16a34a' : '#d97706' }}>
                {coverage}%
              </span>
            </div>
            <div style={{ height: '6px', borderRadius: '3px', background: darkMode ? 'rgba(55,65,81,1)' : 'rgba(229,231,235,1)', overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${coverage}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                style={{
                  height: '100%', borderRadius: '3px',
                  background: coverage === 100
                    ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                    : 'linear-gradient(90deg, #f59e0b, #d97706)',
                }}
              />
            </div>
            {cacheInfo.updatedAt && (
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: darkMode ? 'rgba(107,114,128,1)' : 'rgba(156,163,175,1)' }}>
                <Clock style={{ width: '10px', height: '10px', display: 'inline', marginRight: '4px' }} />
                Generado: {new Date(cacheInfo.updatedAt).toLocaleString('es-AR')}
              </p>
            )}
          </div>
        )}

        {/* Aviso cache desactualizado */}
        {isStale && !loading && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            style={{
              marginBottom: '14px', padding: '10px 14px', borderRadius: '12px',
              background: darkMode ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.35)',
              display: 'flex', alignItems: 'flex-start', gap: '10px',
            }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
            <div>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: darkMode ? '#fbbf24' : '#92400e' }}>
                El checklist fue modificado
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: darkMode ? '#f59e0b' : '#b45309' }}>
                Regenerá el cache para que la voz IA use los ítems actualizados offline.
              </p>
            </div>
          </motion.div>
        )}

        {/* Progreso de generación */}
        {loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{
              marginBottom: '14px', padding: '12px 14px', borderRadius: '12px',
              background: darkMode ? 'rgba(37,99,235,0.1)' : 'rgba(239,246,255,1)',
              border: '1px solid rgba(37,99,235,0.2)',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: jobProgress ? '8px' : 0 }}>
              <Loader style={{ width: '16px', height: '16px', color: '#2563eb', flexShrink: 0, animation: 'spin 1s linear infinite' }} />
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>{progress}</p>
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#3b82f6' }}>
                  No cerrés esta ventana durante la generación
                </p>
              </div>
              {jobProgress && jobProgress.total > 0 && (
                <span style={{ fontSize:'12px', fontWeight:700, color:'#1e40af' }}>
                  {Math.round((jobProgress.done / jobProgress.total) * 100)}%
                </span>
              )}
            </div>
            {jobProgress && jobProgress.total > 0 && (
              <div style={{ height: '6px', borderRadius: '3px', background: darkMode ? 'rgba(55,65,81,1)' : 'rgba(219,234,254,1)', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(jobProgress.done / jobProgress.total) * 100}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  style={{ height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #2563eb, #7c3aed)' }}
                />
              </div>
            )}
          </motion.div>
        )}

        {/* Resultado exitoso */}
        <AnimatePresence>
          {result && !loading && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={{
                marginBottom: '14px', padding: '12px 14px', borderRadius: '12px',
                background: result.errors > 0 ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)',
                border: `1px solid ${result.errors > 0 ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: result.errors > 0 ? '8px' : 0 }}>
                <CheckCircle2 style={{ width: '16px', height: '16px', color: result.errors > 0 ? '#d97706' : '#16a34a', flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: result.errors > 0 ? '#92400e' : '#14532d' }}>
                  {result.generated} audios generados correctamente
                  {result.errors > 0 && ` · ${result.errors} con error`}
                </p>
              </div>
              {result.errors > 0 && result.errorList?.length > 0 && (
                <div>
                  <button onClick={() => setShowItems(p => !p)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#d97706', fontWeight: 600, padding: 0 }}>
                    {showItems ? <ChevronUp style={{ width: '12px', height: '12px' }} /> : <ChevronDown style={{ width: '12px', height: '12px' }} />}
                    Ver ítems con error
                  </button>
                  {showItems && (
                    <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {result.errorList.map((e, i) => (
                        <p key={i} style={{ margin: 0, fontSize: '11px', color: '#92400e', padding: '4px 8px', background: 'rgba(245,158,11,0.1)', borderRadius: '6px' }}>
                          "{e.text}" — {e.error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error */}
        {error && !loading && (
          <div style={{
            marginBottom: '14px', padding: '10px 14px', borderRadius: '12px',
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <AlertCircle style={{ width: '15px', height: '15px', color: '#dc2626', flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '12px', color: '#dc2626', fontWeight: 500 }}>{error}</p>
          </div>
        )}

        {/* Botones */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <motion.button
            onClick={handleGenerate}
            disabled={loading || isLoading || isDemo}
            title={isDemo ? 'No disponible en la demo — regenera el cache completo de la aeronave' : undefined}
            whileHover={!loading && !isLoading && !isDemo ? { scale: 1.02 } : {}}
            whileTap={!loading && !isLoading && !isDemo ? { scale: 0.97 } : {}}
            style={{
              flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '7px', padding: '10px 16px', borderRadius: '12px', border: 'none',
              cursor: (loading || isLoading || isDemo) ? 'not-allowed' : 'pointer',
              background: (loading || isLoading || isDemo) ? 'rgba(107,114,128,0.3)' : 'linear-gradient(135deg, #2563eb, #7c3aed)',
              color: 'white', fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
              opacity: (loading || isLoading || isDemo) ? 0.7 : 1,
            }}
          >
            {loading
              ? <><Loader style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} /> Generando...</>
              : <><Download style={{ width: '14px', height: '14px' }} /> {hasCache ? 'Regenerar cache' : 'Generar audio offline'}</>
            }
          </motion.button>

          {hasCache && (
            <>
              {/* ✅ Botón Sincronizar con feedback visual */}
              <motion.button
                onClick={handleRefreshManifest}
                disabled={loading || syncStatus === 'syncing'}
                whileHover={syncStatus !== 'syncing' ? { scale: 1.04 } : {}}
                whileTap={syncStatus !== 'syncing' ? { scale: 0.96 } : {}}
                title="Descarga el manifiesto de audios a este dispositivo"
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px',
                  borderRadius: '12px', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                  cursor: syncStatus === 'syncing' ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  border: syncStatus === 'success'
                    ? '1px solid rgba(34,197,94,0.35)'
                    : syncStatus === 'error'
                      ? '1px solid rgba(239,68,68,0.35)'
                      : `1px solid ${darkMode ? 'rgba(75,85,99,1)' : 'rgba(209,213,219,1)'}`,
                  background: syncStatus === 'success'
                    ? 'rgba(34,197,94,0.08)'
                    : syncStatus === 'error'
                      ? 'rgba(239,68,68,0.08)'
                      : darkMode ? 'rgba(55,65,81,1)' : 'rgba(249,250,251,1)',
                  color: syncStatus === 'success'
                    ? '#16a34a'
                    : syncStatus === 'error'
                      ? '#dc2626'
                      : darkMode ? 'rgba(209,213,219,1)' : 'rgba(55,65,81,1)',
                }}
              >
                {syncStatus === 'syncing' ? (
                  <><Loader style={{ width: '13px', height: '13px', animation: 'spin 1s linear infinite' }} />Sincronizando...</>
                ) : syncStatus === 'success' ? (
                  <><CheckCircle2 style={{ width: '13px', height: '13px' }} />¡Sincronizado!</>
                ) : syncStatus === 'error' ? (
                  <><AlertCircle style={{ width: '13px', height: '13px' }} />Error al sincronizar</>
                ) : (
                  <><RefreshCw style={{ width: '13px', height: '13px' }} />Sincronizar</>
                )}
              </motion.button>

              <motion.button
                onClick={handleClearCache}
                disabled={loading}
                whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                title="Eliminar cache local de este dispositivo"
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px',
                  borderRadius: '12px', border: '1px solid rgba(239,68,68,0.25)',
                  background: 'rgba(239,68,68,0.06)',
                  color: '#dc2626', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                <Trash2 style={{ width: '13px', height: '13px' }} />
                Limpiar
              </motion.button>
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </motion.div>
  );
};

// ── Componente principal ──────────────────────────────────────────
const AdminAudioCache = ({ darkMode, currentUser }) => {
  const [aircraftList, setAircraftList] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  // Regenerar el cache completo cuesta ~1 síntesis por ítem del checklist;
  // el backend lo bloquea para demo (ver middleware/demoGuard.js). Acá solo
  // grisamos el botón — el gate real está en el back.
  const isDemo = currentUser?.is_demo === true;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await aircraftService.getAll({ is_active: 'true' });
        setAircraftList(res.data?.data || res.data || []);
      } catch { setError('Error al cargar aeronaves'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '14px', background: 'linear-gradient(135deg, #7c3aed, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Volume2 style={{ width: '20px', height: '20px', color: 'white' }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: darkMode ? 'white' : '#111827' }}>
              Audio Offline
            </h2>
            <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(107,114,128,1)' }}>
              Pre-generá la voz IA para usar sin internet
            </p>
          </div>
        </div>

        <div style={{
          padding: '14px 16px', borderRadius: '14px', marginTop: '12px',
          background: darkMode ? 'rgba(37,99,235,0.08)' : 'rgba(239,246,255,1)',
          border: '1px solid rgba(37,99,235,0.15)',
        }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: darkMode ? 'rgba(147,197,253,1)' : '#1e40af', lineHeight: 1.5 }}>
            💡 Al generar el cache, la app llama a ElevenLabs una sola vez por ítem y guarda los audios en la nube.
            En campo, los pilotos escuchan la voz IA <strong>sin necesitar internet</strong>.
            Usá <strong>Sincronizar</strong> en cada dispositivo para descargar el cache al celular.
            Regenerá el cache solo cuando cambie el checklist.
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Loader style={{ width: '28px', height: '28px', color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : error ? (
        <div style={{ padding: '16px', borderRadius: '14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertCircle style={{ width: '18px', height: '18px', color: '#dc2626' }} />
          <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>{error}</p>
        </div>
      ) : aircraftList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', opacity: 0.5 }}>
          <Plane style={{ width: '40px', height: '40px', margin: '0 auto 12px', display: 'block' }} />
          <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(107,114,128,1)' }}>
            No hay aeronaves activas
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {aircraftList.map(aircraft => (
            <AircraftCacheCard
              key={aircraft.id}
              aircraft={aircraft}
              darkMode={darkMode}
              isDemo={isDemo}
              onGenerated={(id) => console.log('[AdminAudioCache] Cache generado para:', id)}
            />
          ))}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  );
};

export default AdminAudioCache;
